import {
  ActionIcon,
  Box,
  Button,
  FileInput,
  Flex,
  Group,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  UnstyledButton,
  useMatches,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "../../UserProfile.module.css";

import { Dropdown, TextSerif } from "@/components/elements";
import { Modal } from "@/components/elements/Modal";
import { ModalEditUserAvatar } from "@/components/elements/UserAvatar/components";
import ModalPreviewUserAvatar from "@/components/elements/UserAvatar/components/ModalPreviewUserAvatar";
import {
  /*   confirmAvatarUpdate, */
  createCroppedImage,
  getCreateAvatarImage,
} from "@/components/elements/UserAvatar/functions";
import {
  IconCalendar,
  IconEdit,
  IconEye,
  IconTrash,
  IconUpload,
  IconUser,
} from "@/components/icons";
import { useUser } from "@/services/auth";
import { useProfile } from "@/services/profile/get-profile";
import { useUpdatePersonalInfo } from "@/services/profile/update-personal-info";
import { useUpdateAvatar } from "@/services/profile/upload-user-image";
import { formatDate } from "@/utils/formatDate";
import { showCustomNotification } from "@/utils/notifications";

import type { UpdatePersonalInfoInput } from "@/services/profile/update-personal-info";
import type { FC } from "react";
import type { Area } from "react-easy-crop";

type FormValues = {
  title: string;
  name: string;
  surname: string;
  gender: string;
  dateOfBirth: Date | null;
  placeOfBirth: string;
  citizenship: string;
  avatarUrl: { url: string; name: string } | null;
};

export const UserProfileTabPersonalInfo: FC = () => {
  const { t } = useTranslation();
  const profileQuery = useProfile();
  const { data: user } = useUser();
  const mutation = useUpdatePersonalInfo();
  const isMobile = useMatches({ base: true, md: false });

  const [opened, setOpened] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    title: "",
    name: "",
    surname: "",
    gender: "",
    dateOfBirth: null,
    placeOfBirth: "",
    citizenship: "",
    avatarUrl: user?.additional_info?.avatarImage || null,
  });
  const [tempFormValues, setTempFormValues] = useState<FormValues>(formValues);

  // avatar upload states
  const [avatarUrl, setAvatarUrl] = useState(profileQuery.data?.additional_info?.avatarImage);
  const [uploadedFileName, setUploadedFileName] = useState<string>(
    user?.additional_info?.avatarImage?.name || ""
  );
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [avatarModalOpened, { open: openAvatarModal, close: closeAvatarModal }] =
    useDisclosure(false);
  const [showAvatarPreviewModal, { open: openAvatarPreviewModal, close: closeAvatarPreviewModal }] =
    useDisclosure(false);

  /* Handlers */
  const createImage = getCreateAvatarImage();
  const getCroppedImg = createCroppedImage(createImage);

  const updateAvatarMutation = useUpdateAvatar({
    mutationConfig: {
      onSuccess: (data) => {
        if (data.additional_info?.avatarImage) {
          const avatar = data.additional_info.avatarImage;
          const normalizedAvatar = {
            url: avatar.url ?? "",
            name: avatar.name ?? "",
          };
          setAvatarUrl(normalizedAvatar);
          setTempFormValues((prev) => {
            const updated = { ...prev, avatarUrl: normalizedAvatar };

            const hasChanges =
              formValues.avatarUrl?.url !== updated.avatarUrl?.url ||
              formValues.avatarUrl?.name !== updated.avatarUrl?.name;

            setIsChanged(hasChanges);
            return updated;
          });
        }
      },
    },
  });

  useEffect(() => {
    // Do not overwrite local unsaved changes or when the edit modal is open.
    if (opened || isChanged) return;

    if (profileQuery.data && !profileQuery.isError) {
      const initialValues: FormValues = {
        title: profileQuery.data.additional_info?.title || "",
        name: profileQuery.data.additional_info?.first_name || "",
        surname: profileQuery.data.additional_info?.last_name || "",
        gender: profileQuery.data.additional_info?.gender || "",
        dateOfBirth: profileQuery.data.additional_info?.date_of_birth
          ? new Date(profileQuery.data.additional_info.date_of_birth)
          : null,
        placeOfBirth: profileQuery.data.additional_info?.place_of_birth || "",
        citizenship: profileQuery.data.additional_info?.citizenship || "",
        avatarUrl: (() => {
          const avatar = profileQuery.data.additional_info?.avatarImage;
          return avatar
            ? { url: avatar.url ?? "", name: avatar.name ?? "" }
            : { url: "", name: "" };
        })(),
      };
      setFormValues(initialValues);
      setTempFormValues(initialValues);
      setAvatarUrl(profileQuery.data.additional_info?.avatarImage);
    }
  }, [profileQuery.data, profileQuery.isError, opened, isChanged]);

  const handleOpen = () => {
    setTempFormValues(formValues);
    setIsChanged(false);
    setOpened(true);
  };

  const handleClose = () => {
    setOpened(false);
    setIsChanged(false);
  };

  const handleChange = <K extends keyof FormValues>(
    field: K,
    value: K extends "dateOfBirth" ? Date | null : FormValues[K] | null
  ) => {
    setTempFormValues((prev) => {
      const updated = {
        ...prev,
        [field]: value ?? (field === "dateOfBirth" ? null : ""),
      };

      const hasChanges = Object.keys(formValues).some((key) => {
        const k = key as keyof FormValues;
        if (k === "dateOfBirth") {
          const d1 = formValues[k]?.getTime?.() ?? null;
          const d2 = updated[k]?.getTime?.() ?? null;
          return d1 !== d2;
        }
        if (k === "avatarUrl") {
          return (
            formValues.avatarUrl?.url !== updated.avatarUrl?.url ||
            formValues.avatarUrl?.name !== updated.avatarUrl?.name
          );
        }
        return formValues[k] !== updated[k];
      });

      setIsChanged(hasChanges);
      return updated;
    });
  };

  const handleSave = () => {
    const validGenders = ["male", "female", "non-binary", "prefer-not-to-say"] as const;

    const payload: UpdatePersonalInfoInput = {
      title: tempFormValues.title,
      first_name: tempFormValues.name,
      last_name: tempFormValues.surname,
      gender: validGenders.includes(tempFormValues.gender as any)
        ? (tempFormValues.gender as UpdatePersonalInfoInput["gender"])
        : undefined,
      date_of_birth: tempFormValues.dateOfBirth
        ? formatDate(tempFormValues.dateOfBirth.toString())
        : "",
      place_of_birth: tempFormValues.placeOfBirth,
      citizenship: tempFormValues.citizenship,
    };

    mutation.mutate(payload, {
      onSuccess: (updatedData) => {
        const updatedValues: FormValues = {
          title: updatedData.additional_info?.title || "",
          name: updatedData.additional_info?.first_name || "",
          surname: updatedData.additional_info?.last_name || "",
          gender: updatedData.additional_info?.gender || "",
          dateOfBirth: updatedData.additional_info?.date_of_birth
            ? new Date(updatedData.additional_info.date_of_birth)
            : null,
          placeOfBirth: updatedData.additional_info?.place_of_birth || "",
          citizenship: updatedData.additional_info?.citizenship || "",
          avatarUrl: updatedData.additional_info?.avatarImage
            ? {
                url: updatedData.additional_info.avatarImage.url || "",
                name: updatedData.additional_info.avatarImage.name || "",
              }
            : null,
        };

        setFormValues(updatedValues);
        setTempFormValues(updatedValues);
        handleClose();
      },
      onError: (error) => console.error(error),
    });
  };

  const handleFileChange = async (file: File | null) => {
    if (file) {
      setUploadedFileName(file.name);
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result as string);
        openAvatarModal();
      });
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleAvatarCancel = () => {
    closeAvatarModal();
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setUploadedFileName("");
  };

  const handleAvatarUpload = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        if (croppedBlob) {
          const croppedUrl = URL.createObjectURL(croppedBlob);
          const newAvatar = { url: croppedUrl, name: uploadedFileName };

          setAvatarUrl(newAvatar);
          handleChange("avatarUrl", newAvatar);

          await updateAvatarMutation.mutateAsync({
            avatarBlob: croppedBlob,
            fileName: uploadedFileName,
          });
        }
      } catch (error) {
        showCustomNotification({
          title: t("profile.error"),
          message: t("profile.error_upload_image"),
          autoClose: 6000,
        });
      }
    }
    closeAvatarModal();
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleAvatarRemove = async () => {
    try {
      await updateAvatarMutation.mutateAsync({
        avatarBlob: new Blob(),
        fileName: "",
      });

      const emptyAvatar = { name: "", url: "" };
      setAvatarUrl(emptyAvatar);
      handleChange("avatarUrl", emptyAvatar);
      setUploadedFileName("");
    } catch (error) {
      showCustomNotification({
        title: t("profile.error"),
        message: t("profile.error_remove_image"),
        autoClose: 6000,
      });
    }
  };

  const additionalInfoEmpty =
    !!profileQuery.data && Object.keys(profileQuery.data.additional_info ?? {}).length === 0;

  if (profileQuery.isLoading || profileQuery.isError) return null;

  return (
    <>
      <Box p="md">
        <Group justify="space-between" align="center" h="2.5em">
          <Title order={3} fw="normal" fz="1.25em">
            {t("profile.navLinks.personal-info")}
          </Title>
          <UnstyledButton>
            <ActionIcon
              radius="sm"
              color="primary"
              variant="light"
              onClick={handleOpen}
              aria-label={t("profile.edit_personal_info")}
            >
              <IconEdit />
            </ActionIcon>
          </UnstyledButton>
        </Group>

        <Stack gap="xs" style={{ flex: 1 }}>
          {[
            { label: t("profile.title"), value: formValues.title },
            { label: t("profile.name"), value: formValues.name },
            { label: t("profile.surname"), value: formValues.surname },
            {
              label: t("profile.gender"),
              value: formValues.gender
                ? formValues.gender.charAt(0).toUpperCase() + formValues.gender.slice(1)
                : "",
            },
            {
              label: t("profile.dob"),
              value: formValues.dateOfBirth ? formatDate(formValues.dateOfBirth.toISOString()) : "",
            },
            { label: t("profile.pob"), value: formValues.placeOfBirth },
            { label: t("profile.citizenship"), value: formValues.citizenship },
          ].map((field) => (
            <Box key={field.label} py="md">
              <Group justify="space-between" align="center" w="100%">
                <TextSerif size="sm">{field.label}</TextSerif>
                <Text size="md">{field.value ?? ""}</Text>
              </Group>
              <Box mt="xs" className={classes.userData_list} />
            </Box>
          ))}
          <Flex py="md" justify="space-between" align="center" w="100%">
            <TextSerif size="sm" mb="xs">
              {t("profile.profile_image")}
            </TextSerif>

            <Flex direction="row" gap="md" pos="relative" align="flex-end">
              {avatarUrl?.name ? (
                <>
                  <Button
                    size="sm"
                    variant={avatarUrl?.name ? "outline" : "transparent"}
                    onClick={avatarUrl?.name ? openAvatarPreviewModal : undefined}
                    disabled={!avatarUrl?.name}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: isMobile ? 150 : 250,
                    }}
                    rightSection={
                      avatarUrl?.name ? (
                        <ActionIcon size="sm">
                          <IconEye />
                        </ActionIcon>
                      ) : undefined
                    }
                    aria-label={t("profile.profile_image")}
                  >
                    {avatarUrl.name}
                  </Button>
                </>
              ) : (
                <Text>{t("profile.nothing_selected")}</Text>
              )}
            </Flex>
          </Flex>
        </Stack>
      </Box>

      <Modal
        opened={opened}
        onClose={handleClose}
        title={
          <Flex align="center" gap="md">
            <ThemeIcon radius="100%" color="primary" variant="light">
              <IconUser />
            </ThemeIcon>
            {t("profile.navLinks.personal-info")}
          </Flex>
        }
        centered
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Stack gap="xl">
            <Stack gap="xs">
              <Dropdown
                label={t("profile.title")}
                placeholder={t("profile.title")}
                data={["Mr.", "Mrs.", "Ms."]}
                value={tempFormValues.title}
                onChange={(val) => handleChange("title", val || "")}
              />
              <TextInput
                label={t("profile.name")}
                placeholder={t("profile.inputFields.enter_name")}
                value={tempFormValues.name}
                onChange={(e) => handleChange("name", e.currentTarget.value)}
              />
              <TextInput
                label={t("profile.surname")}
                placeholder={t("profile.inputFields.enter_surname")}
                value={tempFormValues.surname}
                onChange={(e) => handleChange("surname", e.currentTarget.value)}
              />
              <Dropdown
                label={t("profile.gender")}
                placeholder={t("profile.inputFields.select_gender")}
                data={["male", "female", "non-binary", "prefer-not-to-say"].map((g) => ({
                  value: g,
                  label: g.charAt(0).toUpperCase() + g.slice(1),
                }))}
                value={tempFormValues.gender}
                onChange={(val) => handleChange("gender", val || "")}
              />
              <Flex w="100%" justify="space-between" gap="md">
                <DatePickerInput
                  w="100%"
                  label={t("profile.dob")}
                  placeholder={t("profile.inputFields.enter_dob")}
                  leftSection={<IconCalendar size={24} />}
                  value={tempFormValues.dateOfBirth}
                  onChange={(val) => {
                    const date = val ? new Date(val) : null;
                    handleChange("dateOfBirth", date);
                  }}
                />
                <TextInput
                  w="100%"
                  label={t("profile.pob")}
                  placeholder={t("profile.inputFields.enter_pob")}
                  value={tempFormValues.placeOfBirth}
                  onChange={(e) => handleChange("placeOfBirth", e.currentTarget.value)}
                />
              </Flex>
              <TextInput
                label={t("profile.citizenship")}
                placeholder={t("profile.inputFields.enter_citizenship")}
                value={tempFormValues.citizenship}
                onChange={(e) => handleChange("citizenship", e.currentTarget.value)}
              />
              <Flex align="center" justify={isMobile ? "end" : "space-between"} gap="sm" mt="sm">
                <TextSerif size="sm" mb="md" display={isMobile ? "none" : "block"}>
                  {t("profile.profile_image")}
                </TextSerif>

                <Flex direction="row" gap="md" pos="relative" align="flex-end">
                  {additionalInfoEmpty || !avatarUrl?.name ? (
                    <>
                      <Button
                        variant="transparent"
                        size="sm"
                        loading={updateAvatarMutation.isPending}
                        rightSection={
                          <ActionIcon size="sm">
                            <IconUpload />
                          </ActionIcon>
                        }
                        aria-label={t("profile.upload_image")}
                      >
                        {t("profile.upload_image")}{" "}
                      </Button>
                      <FileInput
                        accept="image/png,image/jpeg,image/gif"
                        onChange={handleFileChange}
                        styles={{
                          root: {
                            position: "absolute",
                            inset: 0,
                          },
                          input: {
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                          },
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const hasAvatarName = !!avatarUrl?.name;
                          if (!hasAvatarName) {
                            openAvatarModal();
                          } else {
                            openAvatarPreviewModal();
                          }
                        }}
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 180,
                        }}
                        rightSection={
                          <ActionIcon size="sm">
                            <IconEye />
                          </ActionIcon>
                        }
                        aria-label={t("profile.upload_image")}
                      >
                        {avatarUrl?.name ? avatarUrl.name : t("profile.upload_image")}
                      </Button>
                    </>
                  )}

                  {avatarUrl?.name && (
                    <UnstyledButton>
                      <ActionIcon
                        size="input-sm"
                        color="red"
                        variant="light"
                        className={classes.actionDeleteIcons}
                        onClick={handleAvatarRemove}
                        aria-label={t("profile.remove_image")}
                      >
                        <IconTrash
                          title={t("profile.remove_image")}
                          aria-label={t("profile.remove_image")}
                        />
                      </ActionIcon>
                    </UnstyledButton>
                  )}
                </Flex>
              </Flex>
            </Stack>
            <Group>
              <Button
                variant="outline"
                onClick={handleClose}
                flex={1}
                aria-label={t("common.cancel")}
              >
                {t("common.cancel")}
              </Button>
              <Button
                color="primary"
                type="submit"
                flex={1}
                disabled={!isChanged}
                loading={mutation.isPending}
                aria-label={t("common.save")}
              >
                {t("common.save")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Upload Modal: This is used for uploading and cropping the new user image */}
      <ModalEditUserAvatar
        opened={avatarModalOpened}
        handleCancel={handleAvatarCancel}
        onCropComplete={onCropComplete}
        crop={crop}
        setCrop={setCrop}
        zoom={zoom}
        setZoom={setZoom}
        imageSrc={imageSrc}
        upload={handleAvatarUpload}
        updateAvatarMutation={updateAvatarMutation}
      />

      <ModalPreviewUserAvatar
        opened={showAvatarPreviewModal}
        avatarUrl={avatarUrl?.url}
        uploadedFileName={uploadedFileName}
        handleCancel={closeAvatarPreviewModal}
      />
    </>
  );
};
